/**
 * Los valores de las variables `{{n}}` de una campaña: o hay dato, o no se envía.
 *
 * EL DEFECTO QUE ESTO CIERRA. `buildRecipientComponents` hacía
 * `{ type: 'text', text: val || ' ' }`: cuando el mapeo estaba vacío mandaba un
 * ESPACIO. No es un relleno, es un valor que FINGE que hay dato donde no lo hay —
 * misma familia que las listas blancas que perdían campos de Meta, un mecanismo que
 * enmascara la ausencia en lugar de rechazarla.
 *
 * Y el resultado de enmascararla es peor que un envío fallido: un mensaje que le
 * llega a un cliente real con un hueco en blanco donde va su nombre.
 *
 * EL ARREGLO NO ES «MANDAR VACÍO EN VEZ DE ESPACIO». Es que el asistente no deje
 * llegar a «Lanzar campaña» con una variable sin resolver. Eso es validación, no
 * fallback.
 *
 * EL ORIGEN DECIDE QUÉ CAMPOS EXISTEN
 * ───────────────────────────────────
 * La lista de contactos sólo tiene `nombre`. El CSV tiene `nombre` más las columnas
 * de ESE fichero. Ofrecer `empresa` a quien eligió contactos de la lista sería
 * ofrecer algo que nunca puede funcionar, y fabricar un `extraFields.empresa` vacío
 * para que "existiera" sería el `|| ' '` otra vez.
 */

/** Campos que un destinatario ofrece según de dónde salió. */
export function camposDisponibles(destinatarios) {
  const campos = ['nombre'];
  const extras = new Set();
  for (const d of destinatarios || []) {
    for (const k of Object.keys(d?.extraFields || {})) extras.add(k);
  }
  return [...campos, ...[...extras].sort()];
}

/**
 * El valor de una variable para un destinatario concreto.
 * @returns {string|null} el valor, o `null` si no hay dato.
 */
export function valorDeVariable(config, destinatario) {
  if (!config) return null;

  if (config.type === 'fixed') {
    const v = String(config.value ?? '').trim();
    return v || null;
  }

  const campo = config.value;
  if (!campo) return null;

  const bruto = campo === 'nombre'
    ? destinatario?.nombre
    : destinatario?.extraFields?.[campo];

  const v = String(bruto ?? '').trim();
  return v || null;
}

/**
 * Comprueba el mapeo contra TODOS los destinatarios.
 *
 * Una columna puede existir en la cabecera del CSV y venir vacía en algunas filas.
 * Esa fila se bloquea con su motivo; no se rellena. Misma regla que el destinatario:
 * o hay dato, o no se envía.
 *
 * @returns {{ok: true} | {ok: false, faltantes: Array<{indice, campo, destinatario}>}}
 */
export function revisarMapeo(varMapping, destinatarios) {
  const entradas = Object.entries(varMapping || {}).sort(([a], [b]) => Number(a) - Number(b));
  if (entradas.length === 0) return { ok: true };

  const faltantes = [];
  for (const [indice, config] of entradas) {
    if (config?.type === 'fixed') {
      // Un valor fijo falta una sola vez, no una por destinatario.
      if (valorDeVariable(config, null) === null) {
        faltantes.push({ indice: Number(indice), campo: null, destinatario: null });
      }
      continue;
    }
    if (!config?.value) {
      faltantes.push({ indice: Number(indice), campo: null, destinatario: null });
      continue;
    }
    for (const d of destinatarios || []) {
      if (valorDeVariable(config, d) === null) {
        faltantes.push({
          indice: Number(indice),
          campo: config.value,
          destinatario: d?.nombre || d?.phone || '',
        });
      }
    }
  }
  return faltantes.length === 0 ? { ok: true } : { ok: false, faltantes };
}

/**
 * Los `components` de Meta para un destinatario.
 *
 * SIN VALOR POR DEFECTO. Si alguna variable no resuelve devuelve `null` y el llamante
 * no envía: llegar aquí con un hueco significa que la validación de arriba falló, y
 * taparlo con un espacio es exactamente lo que se está corrigiendo.
 */
export function componentesParaDestinatario(varMapping, destinatario) {
  const entradas = Object.entries(varMapping || {}).sort(([a], [b]) => Number(a) - Number(b));
  if (entradas.length === 0) return [];

  const parameters = [];
  for (const [, config] of entradas) {
    const valor = valorDeVariable(config, destinatario);
    if (valor === null) return null;
    parameters.push({ type: 'text', text: valor });
  }
  return [{ type: 'body', parameters }];
}
